// User-preference persistence for the Settings page (Phase 6).
//
// V1 stores all preferences in localStorage under a single key. None of
// the fields have backend endpoints yet -- when they do, this module is
// the adapter seam to swap for an API-backed store. Reads tolerate
// malformed JSON / missing keys / unknown enum values by returning the
// declared defaults rather than throwing.

const STORAGE_KEY = "userSettings";

export const COMMISSION_MODELS = ["flat_fee", "per_share", "percentage"] as const;
export type CommissionModel = (typeof COMMISSION_MODELS)[number];

export const FILL_TIME_ROUNDING = ["none", "nearest_minute", "nearest_5min"] as const;
export type FillTimeRounding = (typeof FILL_TIME_ROUNDING)[number];

export const PNL_METHODS = ["fifo", "lifo", "average_cost"] as const;
export type PnlMethod = (typeof PNL_METHODS)[number];

// Scalper roll-up trigger bounds (ADR 0022 D2 follow-on). The
// GET /api/trades/groups endpoint hard-floors window_minutes >= 1 and
// min_run >= 2; the UI tightens the range to realistic intraday scalping
// values. Out-of-range input is clamped to these bounds (see clamp helpers).
export const SCALPER_WINDOW_MIN = 1;
export const SCALPER_WINDOW_MAX = 120;
export const SCALPER_MIN_RUN_MIN = 2;
export const SCALPER_MIN_RUN_MAX = 50;

export type UserSettings = {
  profile: {
    displayName: string;
    timezone: string;
    baseCurrency: string;
  };
  trading: {
    defaultAccountId: string | null;
    commissionModel: CommissionModel;
    fillTimeRounding: FillTimeRounding;
    pnlMethod: PnlMethod;
  };
  display: {
    // Default-ON scalper roll-up (ADR 0022 D2 / UIP-W6): collapse rapid
    // same-symbol runs in the Trades grid into one group row.
    rollUpScalperRuns: boolean;
    // Roll-up trigger thresholds passed to GET /api/trades/groups as
    // window_minutes / min_run. Clamped to the SCALPER_* bounds on read.
    scalperWindowMinutes: number;
    scalperMinRun: number;
  };
};

export const DEFAULT_SETTINGS: UserSettings = {
  profile: {
    displayName: "",
    timezone: typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC",
    baseCurrency: "USD",
  },
  trading: {
    defaultAccountId: null,
    commissionModel: "flat_fee",
    fillTimeRounding: "none",
    pnlMethod: "fifo",
  },
  display: {
    rollUpScalperRuns: true,
    scalperWindowMinutes: 30,
    scalperMinRun: 5,
  },
};

// Snap an arbitrary numeric input to the valid scalper-window range; a
// non-finite value (empty / NaN) falls back to the default. Shared by
// readSettings (tolerant load) and the Display section's clamp-on-blur.
export function clampScalperWindow(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SETTINGS.display.scalperWindowMinutes;
  return Math.min(SCALPER_WINDOW_MAX, Math.max(SCALPER_WINDOW_MIN, Math.round(value)));
}

export function clampScalperMinRun(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SETTINGS.display.scalperMinRun;
  return Math.min(SCALPER_MIN_RUN_MAX, Math.max(SCALPER_MIN_RUN_MIN, Math.round(value)));
}

function asString(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function asEnum<T extends string>(
  value: unknown,
  options: ReadonlyArray<T>,
  fallback: T,
): T {
  if (typeof value === "string" && (options as ReadonlyArray<string>).includes(value)) {
    return value as T;
  }
  return fallback;
}

export function readSettings(): UserSettings {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_SETTINGS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_SETTINGS;
    const root = parsed as Record<string, unknown>;
    const profile = (root.profile ?? {}) as Record<string, unknown>;
    const trading = (root.trading ?? {}) as Record<string, unknown>;
    const display = (root.display ?? {}) as Record<string, unknown>;
    return {
      profile: {
        displayName: asString(profile.displayName, DEFAULT_SETTINGS.profile.displayName),
        timezone: asString(profile.timezone, DEFAULT_SETTINGS.profile.timezone),
        baseCurrency: asString(profile.baseCurrency, DEFAULT_SETTINGS.profile.baseCurrency),
      },
      trading: {
        defaultAccountId:
          typeof trading.defaultAccountId === "string" && trading.defaultAccountId.length > 0
            ? trading.defaultAccountId
            : null,
        commissionModel: asEnum(
          trading.commissionModel,
          COMMISSION_MODELS,
          DEFAULT_SETTINGS.trading.commissionModel,
        ),
        fillTimeRounding: asEnum(
          trading.fillTimeRounding,
          FILL_TIME_ROUNDING,
          DEFAULT_SETTINGS.trading.fillTimeRounding,
        ),
        pnlMethod: asEnum(trading.pnlMethod, PNL_METHODS, DEFAULT_SETTINGS.trading.pnlMethod),
      },
      display: {
        rollUpScalperRuns: asBool(
          display.rollUpScalperRuns,
          DEFAULT_SETTINGS.display.rollUpScalperRuns,
        ),
        scalperWindowMinutes:
          typeof display.scalperWindowMinutes === "number"
            ? clampScalperWindow(display.scalperWindowMinutes)
            : DEFAULT_SETTINGS.display.scalperWindowMinutes,
        scalperMinRun:
          typeof display.scalperMinRun === "number"
            ? clampScalperMinRun(display.scalperMinRun)
            : DEFAULT_SETTINGS.display.scalperMinRun,
      },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function writeSettings(next: UserSettings): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

// Common timezones — IANA list is huge; ship a curated dropdown and let
// the user paste a custom string via the input fallback when needed.
export const COMMON_TIMEZONES: ReadonlyArray<string> = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Hong_Kong",
  "Asia/Singapore",
  "Australia/Sydney",
];

// Common ISO 4217 currencies — the user can also type a 3-letter code.
export const COMMON_CURRENCIES: ReadonlyArray<string> = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "JPY",
  "CHF",
  "HKD",
  "SGD",
];
